import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Button, Glyphicon, OverlayTrigger, Popover } from 'react-bootstrap';
import IntlComponent from 'axway-react-intl';
import { DataTable, PageHeader, SearchBar, ModalDialog } from 'axway-react-ui';

class ModelsListView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.getActions = this.getActions.bind(this);
		this.renderJoins = this.renderJoins.bind(this);
		this.formatModel = this.formatModel.bind(this);
		this.onPluginNotInstalled = this.onPluginNotInstalled.bind(this);
		this.onClose = this.onClose.bind(this);
		this.state = {
			showPluginNotInstalledModal: false
		};
	}

	getActions(model) {
		const tableActions = [];
		const { loading, apiSpecTypes } = this.props;

		if (!model.generated && !model.extended && !model.reduced && !model.invalid) {
			tableActions.push({
				name: this.intl.formatMsg('Edit this model'),
				action: () => this.props.onEditModel(model)
			});
		}

		if (!model.invalid) {
			tableActions.push({
				name: this.intl.formatMsg('Compose into new'),
				action: () => this.props.onComposeModel(model)
			});
		}

		if (!loading) {
			if (apiSpecTypes.openapi) {
				tableActions.push({
					name: this.intl.formatMsg('Generate API'),
					action: () => this.props.onGenerateEndpoints(model)
				});
			} else if (window.$conf.endpointsEnabled) {
				tableActions.push({
					name: this.intl.formatMsg('Generate endpoints'),
					action: () => this.props.onGenerateEndpoints(model)
				});
			} else {
				tableActions.push({
					name: this.intl.formatMsg('Generate API'),
					action: () => this.onPluginNotInstalled()
				});
			}
		}
		return tableActions;
	}

	onPluginNotInstalled() {
		this.setState({
			showPluginNotInstalledModal: true
		});
	}

	onClose() {
		this.setState({
			showPluginNotInstalledModal: false
		});
	}

	/**
	 * Render the content of the joins column.
	 */
	renderJoins(item, rowComp) {
		if (!item.joins.length) {
			return null;
		}

		const fieldModelLinks = item.joins.sort().map(m => (
			<div key={m}>
				<a
					href={`${window.$conf.prefix}/project/models/${encodeURIComponent(m)}`}
					onClick={
						(e) => {
							e.preventDefault();
							this.props.onViewModel(m);
						}
					}
				>
					{m}
				</a>
			</div>
		));

		const compositePopover = (
			<Popover id={`models-list-popover-${encodeURIComponent(item.name)}`}>
				{fieldModelLinks}
			</Popover>
		);

		return (
			<div className="model-joins">
				<span className="model-count">
					{this.intl.formatMsg('{count} {count, plural, one {model} other {models}}', { count: item.joins.length })}
				</span>

				<OverlayTrigger
					container={rowComp}
					rootClose
					trigger={[ 'click' ]}
					placement="left"
					overlay={compositePopover}
				>
					<Button
						bsStyle="link"
						onClick={(e) => { e.stopPropagation(); }}
						onKeyDown={(e) => { e.stopPropagation(); }}
					>
						<Glyphicon glyph=" gg icon-GG-info" />
					</Button>
				</OverlayTrigger>
			</div>
		);
	}

	formatModel(model, key) {
		const joinmeta = model._joinmeta_ || {};
		const joins = (joinmeta.joinedModels || []).map(m => {
			return m;
		});
		if (joinmeta.parent) {
			joins.push(joinmeta.parent);
		}

		return {
			key,
			name: model.name,
			connector: (model.connector && model.connector.name) || '',
			description: model.description || '',
			generated: !!model.generated,
			extended: !!model._extended,
			reduced: !model._extended && model._supermodel,
			joins,
			invalid: joinmeta.error
		};
	}

	renderPluginNotInstalledModal() {
		const { showPluginNotInstalledModal } = this.state;
		if (!showPluginNotInstalledModal) {
			return;
		}
		return (
			<ModalDialog
				title={this.intl.formatMsg('Sorry, this feature is disabled')}
				show
				closeText={this.intl.formatMsg('OK')}
				onClose={this.onClose}
			>
				<p>
					{this.intl.formatMsg('To enable generation of API from models, you need to install the OpenAPI flow-trigger plugin from the Plugins page.')}
				</p>
			</ModalDialog>
		);
	}

	render() {
		const { models, loading } = this.props;

		let items;
		if (models) {
			items = models.map(this.formatModel)
				.sort((a, b) => a.name.localeCompare(b.name));
		}

		const columns = [
			{
				key: 'name',
				name: this.intl.formatMsg('Model name'),
				className: 'name',
				format: (item) => {
					const name = encodeURIComponent(item.name);
					return (
						<a
							href={`${window.$conf.prefix}/project/models/${name}`}
							onClick={
								(e) => {
									e.preventDefault();
								}
							}
						>
							{item.name}
						</a>
					);
				}
			},
			{
				key: 'connector',
				name: this.intl.formatMsg('Connector name'),
				className: 'connector'
			},
			{
				key: 'description',
				name: this.intl.formatMsg('Description'),
				className: 'description'
			},
			{
				key: 'joins',
				name: this.intl.formatMsg('Sources'),
				className: (item) => {
					let cn = 'joins';
					if (item && item.joins.length) {
						cn += ' row-clickable';
					}
					return cn;
				},
				format: this.renderJoins
			},
			{
				key: 'rowActions',
				className: 'row-actions'
			}
		];

		const modelInfo
			= this.intl.formatMsg(
				'A Model is an abstraction of data from a data source. A model can consist of other models or fields from other models. The data can then be queried and manipulated via API, or by their model flow-node in the flow editor.');

		return (
			<div className="models-list-view">
				<PageHeader>
					<PageHeader.Section align="left">
						<PageHeader.Title
							infoPopover={{
								id: 'models-info',
								description: modelInfo,
								docLink: window.$conf.docModelUrl
							}}
						>
							{this.intl.formatMsg('Models')}
						</PageHeader.Title>
					</PageHeader.Section>
					<PageHeader.Section>
						<SearchBar
							onChange={this.props.onSearch}
							throttle={50}
						/>
					</PageHeader.Section>
					<PageHeader.Section align="right">
						<Button
							bsSize="large"
							bsStyle="primary"
							className="compact"
							onClick={this.props.onCreateModel}
						>
							<Glyphicon glyph=" gg icon-GG-plus" /> {this.intl.formatMsg('Model')}
						</Button>
					</PageHeader.Section>
				</PageHeader>
				{this.renderPluginNotInstalledModal()}
				<DataTable
					columns={columns}
					items={items}
					loading={loading}
					className="ux-container"
					clickable
					rowActions={this.getActions}
					rowClick={(e, i) => this.props.onViewModel(i)}
				/>
			</div>
		);
	}
}

ModelsListView.propTypes = {
	models: PropTypes.array.isRequired,
	apiSpecTypes: PropTypes.object,
	loading: PropTypes.bool,
	onSearch: PropTypes.func.isRequired,
	onViewModel: PropTypes.func.isRequired,
	onCreateModel: PropTypes.func.isRequired,
	onEditModel: PropTypes.func.isRequired,
	onComposeModel: PropTypes.func.isRequired
};

ModelsListView.defaultProps = {
	apiSpecTypes: {}
};

export default injectIntl(ModelsListView);
